import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import styled from "styled-components";
import { FormikProvider, useFormik } from "formik";

import { CardBody } from "../../../components/DashboardCard/DashboardCard";
import Button from "../../../components/Button/Button";
import TextField from "../../../components/FormFields/TextField";
import ToggleSwitchField from "../../../components/FormFields/ToggleSwitchField";
import StickyCard, {
	StickyCardBody,
	StickyCardHeader,
	StickyHeaderBar,
} from "../../../components/StickyCard/StickyCard";
import { useAlert } from "../../../components/Alert/Alerts";
import Icon from "../../../components/Icon/Icon";
import SearchBar from "../../../components/SearchBar/SearchBar";
import ThemisRuleConditionsBuilder from "./ThemisRuleConditionsBuilder";

import {
	getThemisRule,
	updateThemisRule,
	ThemisRuleDto,
} from "./manageThemisRules";
import ThemisRuleEffectsBuilder from "./ThemisRuleEffectsBuilder";
import {
	getReachableCatalog,
	searchFields,
	ThemisCatalogFieldSearchResultRowDto,
} from "./manageThemisRuleConditions";
import { ThemisConditionValueType } from "./ThemisInterfaces";
import { ThemisFieldPickerRequest } from "./themisFieldPickerTypes";

const ReadOnlyRow = styled.div`
	display: grid;
	grid-template-columns: 180px 1fr;
	gap: 1rem;
	padding: 0.5rem 0;
	opacity: 0.8;

	.label {
		font-weight: 600;
	}
`;

const PageGrid = styled.div`
	display: grid;
	grid-template-columns: minmax(0, 1fr) 500px;
	gap: 1.5rem;
	align-items: start;
	width: 100%;

	.side-sticky {
		position: sticky;
		top: calc(163px + 2rem) !important;
		align-self: start;
	}

	@media only screen and (max-width: 1180px) {
		grid-template-columns: 1fr;

		.side-sticky {
			position: relative;
			top: 0 !important;
			width: auto;
			order: 0;
		}
	}
`;

const PageShell = styled.div`
	width: min(100%, 1440px);
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
`;

const PageHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1rem;
	flex-wrap: wrap;
`;

const MainColumn = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
	min-width: 0;
	width: 100%;
`;

const OptionsSection = styled.div`
	display: flex;
	width: 100%;
	padding: 1.5rem;
	border: 1px solid ${({ theme }) => theme.colorBorderLight};
	background: ${({ theme }) => theme.colorBackgroundLightLight};
	box-shadow: 0 2px 10px 0 ${({ theme }) => theme.colorBoxShadow};
`;

const PickerRow = styled.button`
	width: 100%;
	border: 1px solid ${({ theme }) => theme.colorBorderLight};
	background: ${({ theme }) => theme.colorBackgroundLight};
	box-shadow: 0 2px 8px 0 ${({ theme }) => theme.colorBoxShadow};
	padding: 0.75rem;
	text-align: left;
	cursor: pointer;
	transition:
		border-color 140ms ease-in,
		background 140ms ease-in;

	&:hover {
		border-color: ${({ theme }) => theme.colorActivation};
		background: ${({ theme }) => theme.colorBackgroundLightLight};
	}

	.key {
		font-size: 0.82rem;
		opacity: 0.75;
	}

	.scope {
		font-size: 0.78rem;
		opacity: 0.65;
	}
`;

const PickerContext = styled.div`
	border: 1px solid ${({ theme }) => theme.colorBorderLight};
	background: ${({ theme }) => theme.colorBackgroundLightLight};
	padding: 0.75rem;
	display: flex;
	flex-direction: column;
	gap: 0.45rem;

	.label {
		font-size: 0.72rem;
		font-weight: 700;
		letter-spacing: 0.08em;
		text-transform: uppercase;
		opacity: 0.58;
	}

	.value {
		font-size: 0.92rem;
		font-weight: 600;
	}
`;

const PickerEmptyState = styled.div`
	border: 1px dashed ${({ theme }) => theme.colorBorderLight};
	background: ${({ theme }) => theme.colorBackgroundLightLight};
	padding: 1rem;
	font-size: 0.9rem;
	opacity: 0.75;
`;

const valueTypeToInt = (vt: ThemisConditionValueType): number => {
	switch (vt) {
		case ThemisConditionValueType.String:
			return 0;
		case ThemisConditionValueType.Number:
			return 1;
		case ThemisConditionValueType.Boolean:
			return 2;
		case ThemisConditionValueType.DateTimeUtcMs:
			return 3;
		case ThemisConditionValueType.Int:
			return 4;
		case ThemisConditionValueType.Decimal:
			return 5;
		default:
			return 0;
	}
};

type RouteParams = {
	ruleId: string;
};

const ThemisRuleEdit = () => {
	const { ruleId } = useParams<RouteParams>();
	const id = useMemo(() => Number(ruleId), [ruleId]);

	const history = useHistory();
	const { addNewAlert } = useAlert();

	const [loading, setLoading] = useState(true);
	const [rule, setRule] = useState<ThemisRuleDto | null>(null);
	const [pickerRequest, setPickerRequest] =
		useState<ThemisFieldPickerRequest | null>(null);
	const [pickerQ, setPickerQ] = useState("");
	const [pickerBasicScope, setPickerBasicScope] = useState("");
	const [pickerBasicScopes, setPickerBasicScopes] = useState<string[]>([]);
	const [pickerSearching, setPickerSearching] = useState(false);
	const [pickerUiKey, setPickerUiKey] = useState(0);
	const [pickerResults, setPickerResults] = useState<
		ThemisCatalogFieldSearchResultRowDto[]
	>([]);
	const pickerResolverRef = useRef<
		((row: ThemisCatalogFieldSearchResultRowDto | null) => void) | null
	>(null);
	const pickerSearchSeqRef = useRef(0);

	const formik = useFormik({
		initialValues: {
			name: "",
			description: "",
			isEnabled: false,
			stopProcessing: false,
		},
		enableReinitialize: true,
		onSubmit: () => {},
	});

	const applyRuleToForm = (nextRule: ThemisRuleDto) => {
		setRule(nextRule);
		formik.setValues({
			name: nextRule.name ?? "",
			description: nextRule.description ?? "",
			isEnabled: !!nextRule.isEnabled,
			stopProcessing: !!nextRule.stopProcessing,
		});
	};

	const refreshRule = async (ruleToRefresh: number) => {
		const refreshed = await getThemisRule(ruleToRefresh);
		applyRuleToForm(refreshed.data);
	};

	const resolvePicker = useCallback(
		(result: ThemisCatalogFieldSearchResultRowDto | null) => {
			const resolver = pickerResolverRef.current;
			pickerResolverRef.current = null;
			setPickerRequest(null);
			setPickerSearching(false);
			setPickerResults([]);
			if (resolver) {
				resolver(result);
			}
		},
		[],
	);

	const pickField = useCallback((req: ThemisFieldPickerRequest) => {
		return new Promise<ThemisCatalogFieldSearchResultRowDto | null>(
			(resolve) => {
				if (pickerResolverRef.current) {
					pickerResolverRef.current(null);
				}
				pickerResolverRef.current = resolve;
				setPickerRequest(req);
				setPickerQ("");
				setPickerBasicScope("");
				setPickerResults([]);
				setPickerUiKey((current) => current + 1);
			},
		);
	}, []);

	useEffect(() => {
		if (!id || Number.isNaN(id)) {
			addNewAlert({ type: "error", message: "Invalid rule id." });
			return;
		}

		setLoading(true);
		getThemisRule(id)
			.then((res) => {
				applyRuleToForm(res.data);
			})
			.catch(() => {
				addNewAlert({ type: "error", message: "Failed to load rule." });
			})
			.finally(() => setLoading(false));
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [id]);

	useEffect(() => {
		if (!rule?.targetScopeKey || !rule.phase) return;

		getReachableCatalog({
			rootScopeKey: rule.targetScopeKey,
			phaseKey: rule.phase,
		})
			.then((res) => {
				const scopes = res.data?.scopes ?? [];
				const nextScopes = Array.from(
					new Set(
						scopes
							.map((scope) => {
								const base = (
									scope.baseScopeKey ??
									scope.key?.split(":")[0] ??
									""
								).trim();
								return base || null;
							})
							.filter((x): x is string => !!x),
					),
				).sort((a, b) => a.localeCompare(b));
				setPickerBasicScopes(nextScopes);
			})
			.catch(() => {
				setPickerBasicScopes([]);
			});
	}, [rule?.targetScopeKey, rule?.phase]);

	useEffect(() => {
		if (!rule?.targetScopeKey || !rule.phase || !pickerRequest) return;

		const seq = ++pickerSearchSeqRef.current;
		const handle = setTimeout(() => {
			setPickerSearching(true);
			searchFields({
				rootScopeKey: rule.targetScopeKey,
				phaseKey: rule.phase,
				q: pickerQ.trim() || undefined,
				scopeKey: pickerBasicScope || undefined,
				valueType:
					pickerRequest.requiredValueType != null
						? valueTypeToInt(pickerRequest.requiredValueType)
						: undefined,
				page: 1,
				recordsPerPage: 60,
			})
				.then((res) => {
					if (pickerSearchSeqRef.current !== seq) return;
					setPickerResults(res.data?.results ?? []);
				})
				.catch(() => {
					if (pickerSearchSeqRef.current !== seq) return;
					setPickerResults([]);
				})
				.finally(() => {
					if (pickerSearchSeqRef.current !== seq) return;
					setPickerSearching(false);
				});
		}, 180);

		return () => clearTimeout(handle);
	}, [
		rule?.targetScopeKey,
		rule?.phase,
		pickerRequest,
		pickerQ,
		pickerBasicScope,
	]);

	useEffect(() => {
		return () => {
			if (pickerResolverRef.current) {
				pickerResolverRef.current(null);
				pickerResolverRef.current = null;
			}
		};
	}, []);

	const handleSave = async () => {
		if (!rule) return;

		try {
			await updateThemisRule(rule.id, {
				name: formik.values.name,
				description: formik.values.description,
				priority: rule.priority, // required by your Update DTO
				isEnabled: formik.values.isEnabled,
				stopProcessing: formik.values.stopProcessing,
			});

			addNewAlert({ type: "success", message: "Rule saved." });
			await refreshRule(rule.id);
		} catch {
			addNewAlert({ type: "error", message: "Failed to save rule." });
		}
	};

	if (loading) {
		return (
			<CardBody>
				<p>Loading...</p>
			</CardBody>
		);
	}

	if (!rule) {
		return (
			<CardBody>
				<p>Rule not found.</p>
				<Button onClick={() => history.push("/themis")}>Back</Button>
			</CardBody>
		);
	}

	return (
		<FormikProvider value={formik}>
			<CardBody className="flex flex-col items-start gap-[1.5rem]">
				<PageShell>
					<PageHeader>
						<h2 className="m-0">Edit Rule</h2>
						<div className="flex gap-[.75rem]">
							<Button onClick={() => history.goBack()}>Back</Button>
							<Button onClick={handleSave}>Save</Button>
						</div>
					</PageHeader>
					<PageGrid>
						<MainColumn>
							<OptionsSection>
								<div className="w-full flex flex-col gap-[1rem]">
									<TextField
										name="name"
										placeholder="Name"
										value={formik.values.name}
										onChange={(e) =>
											formik.setFieldValue("name", e.target.value)
										}
									/>

									<TextField
										name="description"
										placeholder="Description"
										component="textarea"
										height="7rem"
										value={formik.values.description}
										onChange={(e) =>
											formik.setFieldValue("description", e.target.value)
										}
									/>

									<div className="flex gap-[2rem] items-center">
										<ToggleSwitchField
											id="enabled"
											name="isEnabled"
											label="Enabled"
											checked={formik.values.isEnabled}
											onChange={(e) =>
												formik.setFieldValue("isEnabled", e.target.checked)
											}
										/>

										<ToggleSwitchField
											id="stopProcessing"
											name="stopProcessing"
											label="Stop Processing"
											checked={formik.values.stopProcessing}
											onChange={(e) =>
												formik.setFieldValue("stopProcessing", e.target.checked)
											}
										/>
									</div>

									<div className="mt-[.5rem]">
										<ReadOnlyRow>
											<div className="label">Phase</div>
											<div>{rule.phase}</div>
										</ReadOnlyRow>
										<ReadOnlyRow>
											<div className="label">Target Scope</div>
											<div>{rule.targetScopeKey}</div>
										</ReadOnlyRow>
										<ReadOnlyRow>
											<div className="label">Priority</div>
											<div>{rule.priority}</div>
										</ReadOnlyRow>
									</div>
								</div>
							</OptionsSection>

							<ThemisRuleConditionsBuilder
								ruleId={rule.id}
								phaseKey={rule.phase}
								targetScopeKey={rule.targetScopeKey}
								groups={rule.conditionGroups ?? []}
								pickField={pickField}
								onReload={async () => {
									await refreshRule(rule.id);
								}}
							/>

							<ThemisRuleEffectsBuilder
								ruleId={rule.id}
								phaseKey={rule.phase}
								targetScopeKey={rule.targetScopeKey}
								effects={rule.effects ?? []}
								pickField={pickField}
								onReload={async () => {
									await refreshRule(rule.id);
								}}
							/>
						</MainColumn>

						<StickyCard className="side-sticky !min-h-fit xl:!w-[500px] xl:!min-w-[500px] xl:!max-w-[500px]">
							<StickyCardHeader>
								<span className="card-title">
									{pickerRequest?.title ?? "Field Catalog"}
								</span>
								{pickerRequest ? (
									<div className="flex flex-wrap gap-[1.5625rem]">
										<Icon
											icon="close"
											color="currentColor"
											width="24px"
											height="24px"
											onClick={() => resolvePicker(null)}
										/>
									</div>
								) : null}
							</StickyCardHeader>
							{pickerRequest ? (
								<>
									<StickyHeaderBar className="!p-[1rem]">
										<SearchBar
											key={`picker_search_${pickerUiKey}`}
											className="w-full"
											label="Search catalog fields"
											searchPlaceholder="Search"
											value={pickerQ}
											onChange={(e) => setPickerQ(e.target.value)}
											dropdown={{
												name: "pickerBasicScope",
												options: [
													{ label: "All basic scopes", value: "" },
													...pickerBasicScopes.map((scope) => ({
														label: scope,
														value: scope,
													})),
												],
												onChange: (value) =>
													setPickerBasicScope(String(value ?? "")),
											}}
										/>
									</StickyHeaderBar>
									<StickyCardBody className="!p-[1rem] !pt-0 flex flex-col gap-[.75rem]">
										<PickerContext>
											<div>
												<div className="label">Target Scope</div>
												<div className="value">{rule.targetScopeKey}</div>
											</div>
											<div>
												<div className="label">Value Type Filter</div>
												<div className="value">
													{pickerRequest.requiredValueType ?? "Any"}
												</div>
											</div>
										</PickerContext>

										{pickerSearching ? (
											<p className="m-0 text-sm">Loading fields...</p>
										) : null}

										<div className="flex flex-col gap-[.5rem] max-h-[26rem] overflow-auto">
											{pickerResults.length > 0 ? (
												pickerResults.map((field) => (
													<PickerRow
														key={`picker_field_${field.fieldKey}`}
														onClick={() => resolvePicker(field)}
													>
														<div className="font-semibold">
															{field.fieldDisplayName || field.fieldKey}
														</div>
														<div className="key">{field.fieldKey}</div>
														<div className="scope">
															{field.scopeDisplayName}
														</div>
													</PickerRow>
												))
											) : (
												<PickerEmptyState>No fields found.</PickerEmptyState>
											)}
										</div>
									</StickyCardBody>
								</>
							) : (
								<StickyCardBody className="!p-[1rem] flex flex-col gap-[.75rem]">
									<PickerEmptyState>
										Select a field button from a condition or effect to browse
										matching catalog fields here.
									</PickerEmptyState>
								</StickyCardBody>
							)}
						</StickyCard>
					</PageGrid>
				</PageShell>
			</CardBody>
		</FormikProvider>
	);
};

export default ThemisRuleEdit;
